import { useEffect, useMemo, useState } from 'react';
import './LandingPage.css';
import { getSectorSummary } from '../api/client';

export default function SectorLanding({ session, onLogout }) {
  const { user, token } = session;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await getSectorSummary(token);
      setData(response);
      setError('');
    } catch (err) {
      setError(err.message || 'Unable to load sector summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role_id === 4) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.role_id]);

  const totals = useMemo(
    () => ({
      units: data?.totals?.units ?? 0,
      members: data?.totals?.members ?? 0,
      over18: data?.totals?.over_18 ?? 0,
      contributors: data?.totals?.contributors ?? 0,
      collected: Number(data?.totals?.collected ?? 0),
      participatingUnits: data?.totals?.participating_units ?? 0,
    }),
    [data]
  );

  const units = data?.units ?? [];

  return (
    <div className="landing-container">
      <header className="landing-header">
        <div>
          <h1 className="landing-title">Sector dashboard</h1>
          <p className="landing-subtitle">
            Monitor units under your sector and their sadaqa participation.
          </p>
        </div>
        <button type="button" className="landing-logout" onClick={onLogout}>
          Log out
        </button>
      </header>

      <section className="landing-content">
        {error ? (
          <div className="inline-message error">
            <span>{error}</span>
            <button type="button" className="btn outline" onClick={loadData} disabled={loading}>
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="summary-grid">
              <div className="summary-card">
                <h3>Total units</h3>
                <div className="value">{totals.units}</div>
                <div className="meta">Units managed by this sector</div>
              </div>
              <div className="summary-card">
                <h3>Participating units</h3>
                <div className="value">{totals.participatingUnits}</div>
                <div className="meta">Units with recorded contributions</div>
              </div>
              <div className="summary-card">
                <h3>Members</h3>
                <div className="value">{totals.members}</div>
                <div className="meta">Total members across units</div>
              </div>
              <div className="summary-card">
                <h3>Members aged 18+</h3>
                <div className="value">{totals.over18}</div>
                <div className="meta">Adult members across units</div>
              </div>
              <div className="summary-card">
                <h3>Contributors</h3>
                <div className="value">{totals.contributors}</div>
                <div className="meta">Unique members who contributed</div>
              </div>
              <div className="summary-card">
                <h3>Total collected</h3>
                <div className="value">₹{totals.collected.toLocaleString()}</div>
                <div className="meta">Overall sadaqa collected</div>
              </div>
            </div>

            <section>
              <h2>Unit participation</h2>
              {loading ? (
                <div className="inline-message">Loading units…</div>
              ) : (
                <table className="contrib-table">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      <th>Members</th>
                      <th>Members 18+</th>
                      <th>Contributors</th>
                      <th>Total collected</th>
                      <th>Participated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {units.length === 0 ? (
                      <tr>
                        <td colSpan={6}>No units found under this sector.</td>
                      </tr>
                    ) : (
                      units.map((unit) => (
                        <tr key={unit.id}>
                          <td>{unit.orgname}</td>
                          <td>{unit.members_count}</td>
                          <td>{unit.over_18_count}</td>
                          <td>{unit.contributors_count}</td>
                          <td>₹{Number(unit.total_collected || 0).toLocaleString()}</td>
                          <td>{unit.participated ? 'Yes' : 'No'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </section>
          </>
        )}
      </section>
    </div>
  );
}
